import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";

type PaymentConfirmationProps = {
  bookingData: {
    treatmentId: string;
    treatment: any;
    preferredDate: string;
    facility: string;
    patientName: string;
    patientPhone: string;
    patientEmail: string;
    emergencyContact: string;
    emergencyPhone: string;
    provider?: string;
    medicalHistory?: string;
  };
  onConfirm: () => void;
};

export default function PaymentConfirmation({ bookingData, onConfirm }: PaymentConfirmationProps) {
  const { treatment } = bookingData;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Confirm Treatment Booking</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Treatment Summary */}
          <div>
            <h3 className="font-semibold mb-3">Treatment Details</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Treatment:</span>
                <span className="font-medium">{treatment.title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Category:</span>
                <Badge variant="secondary">{treatment.category}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Duration:</span>
                <span>{treatment.duration}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Estimated Cost:</span>
                <span className="font-bold text-lg">AED {treatment.price.toLocaleString()}</span>
              </div>
            </div>
          </div>

          <Separator />

          {/* Booking Details */}
          <div>
            <h3 className="font-semibold mb-3">Booking Information</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Preferred Date:</span>
                <span>{new Date(bookingData.preferredDate).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Facility:</span>
                <span>{bookingData.facility}</span>
              </div>
            </div>
          </div>

          <Separator />

          {/* Patient Information */}
          <div>
            <h3 className="font-semibold mb-3">Patient Information</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Patient Name:</span>
                <span>{bookingData.patientName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Email:</span>
                <span>{bookingData.patientEmail}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Phone:</span>
                <span>{bookingData.patientPhone}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Emergency Contact:</span>
                <span>{bookingData.emergencyContact}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Emergency Phone:</span>
                <span>{bookingData.emergencyPhone}</span>
              </div>
            </div>
          </div>

          {bookingData.medicalHistory && (
            <>
              <Separator />
              <div>
                <h3 className="font-semibold mb-3">Medical History / Notes</h3>
                <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                  {bookingData.medicalHistory}
                </p>
              </div>
            </>
          )}

          <Separator />

          {/* Payment Method */}
          <div>
            <h3 className="font-semibold mb-3">Payment Method</h3>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Selected Provider:</span>
              <Badge variant="outline" className="capitalize">
                {bookingData.provider || "Not selected"}
              </Badge>
            </div>
          </div>

          <div className="bg-muted p-4 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> This is an estimate. Final billing may vary based on actual treatment requirements and facility charges. 
              You will be redirected to your selected payment provider to complete the financing application.
            </p>
          </div>

          <Button 
            onClick={onConfirm} 
            className="w-full"
            size="lg"
          >
            Confirm & Proceed to Payment
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}